import React, { useState, useEffect, useCallback } from 'react';
import {
  Tabs, Table, Button, Typography, Space, Popconfirm, message,
} from 'antd';
import Rest from 'tools/rest';
import Preloader from 'components/preloader';
import CategoryFormModal from './categoryFormModal';
import WarehouseFormModal from './warehouseFormModal';
import MaterialFormModal from './materialFormModal';

const { Text } = Typography;

const ReferenceTab = ({ dataVersion, onDataChange }) => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [catModalVisible, setCatModalVisible] = useState(false);
  const [whModalVisible, setWhModalVisible] = useState(false);
  const [matModalVisible, setMatModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [editingMaterial, setEditingMaterial] = useState(null);

  const loadAll = useCallback(() => {
    setLoading(true);
    let pending = 3;
    const done = () => {
      pending -= 1;
      if (pending <= 0) setLoading(false);
    };
    Rest.get('/api/v1/material_categories.json').then((r) => {
      setCategories(r.data.material_categories || []);
      done();
    }).catch(() => done());
    Rest.get('/api/v1/warehouses.json').then((r) => {
      setWarehouses(r.data.warehouses || []);
      done();
    }).catch(() => done());
    Rest.get('/api/v1/materials.json', { params: { page: 1, per: 5000 } }).then((r) => {
      setMaterials(r.data.materials || []);
      done();
    }).catch(() => done());
  }, []);

  useEffect(() => {
    loadAll();
  }, [dataVersion, loadAll]);

  const deleteErrorText = (err) => {
    const d = err.response?.data;
    if (d?.errors) {
      return Array.isArray(d.errors)
        ? d.errors.join(', ')
        : Object.values(d.errors).flat().join(', ');
    }
    return 'Не удалось удалить';
  };

  const afterDelete = () => {
    loadAll();
    if (onDataChange) onDataChange();
    message.success('Удалено');
  };

  const handleDeleteCategory = (id) => {
    Rest.delete(`/api/v1/material_categories/${id}.json`)
      .then((resp) => {
        if (resp.data?.errors) {
          message.error(
            Array.isArray(resp.data.errors)
              ? resp.data.errors.join(', ')
              : deleteErrorText({ response: { data: resp.data } })
          );
          return;
        }
        afterDelete();
      })
      .catch((err) => message.error(deleteErrorText(err)));
  };

  const handleDeleteWarehouse = (id) => {
    Rest.delete(`/api/v1/warehouses/${id}.json`)
      .then((resp) => {
        if (resp.data?.errors) {
          message.error(
            Array.isArray(resp.data.errors)
              ? resp.data.errors.join(', ')
              : deleteErrorText({ response: { data: resp.data } })
          );
          return;
        }
        afterDelete();
      })
      .catch((err) => message.error(deleteErrorText(err)));
  };

  const handleDeleteMaterial = (id) => {
    Rest.delete(`/api/v1/materials/${id}.json`)
      .then((resp) => {
        if (resp.data?.errors) {
          message.error(
            Array.isArray(resp.data.errors)
              ? resp.data.errors.join(', ')
              : deleteErrorText({ response: { data: resp.data } })
          );
          return;
        }
        afterDelete();
      })
      .catch((err) => message.error(deleteErrorText(err)));
  };

  const catColumns = [
    { title: 'Название', dataIndex: 'name', key: 'name' },
    {
      title: '',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditingCategory(record);
              setCatModalVisible(true);
            }}
          >
            Изменить
          </Button>
          {record.deletable === true ? (
            <Popconfirm
              title="Удалить категорию?"
              description="Удаление доступно, пока к категории не привязаны виды материалов."
              onConfirm={() => handleDeleteCategory(record.id)}
              okText="Удалить"
              cancelText="Отмена"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" size="small" danger>
                Удалить
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  const whColumns = [
    { title: 'Название', dataIndex: 'name', key: 'name', width: '28%' },
    {
      title: 'Автоподтверждение',
      dataIndex: 'auto_approve',
      key: 'auto_approve',
      width: '16%',
      render: (v) => (v ? 'Да' : 'Нет'),
    },
    {
      title: 'Поля размещения',
      dataIndex: 'temp_storage',
      key: 'temp_storage',
      render: (ts) => {
        if (!ts || typeof ts !== 'object' || Object.keys(ts).length === 0) {
          return <Text type="secondary">Стеллаж · полка</Text>;
        }
        const labels = Object.values(ts)
          .map((v) => (v != null ? String(v).trim() : ''))
          .filter(Boolean);
        return (
          <span style={{ fontSize: 13 }}>
            {labels.length ? labels.join(' · ') : '—'}
          </span>
        );
      },
    },
    {
      title: '',
      key: 'wh_actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditingWarehouse(record);
              setWhModalVisible(true);
            }}
          >
            Изменить
          </Button>
          {record.deletable === true ? (
            <Popconfirm
              title="Удалить склад?"
              description="Доступно только для склада без накладных и без остатков по позициям."
              onConfirm={() => handleDeleteWarehouse(record.id)}
              okText="Удалить"
              cancelText="Отмена"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" size="small" danger>
                Удалить
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  const matColumns = [
    { title: 'Наименование', dataIndex: 'name', key: 'name' },
    { title: 'Категория', dataIndex: 'category_name', key: 'cat', render: (v) => v || '—' },
    { title: 'Ед. изм.', dataIndex: 'unit', key: 'unit', width: 100, render: (v) => v || '—' },
    {
      title: '',
      key: 'mat_actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            onClick={() => {
              setEditingMaterial(record);
              setMatModalVisible(true);
            }}
          >
            Изменить
          </Button>
          {record.deletable === true ? (
            <Popconfirm
              title="Удалить позицию из реестра?"
              description="Пока нет остатков на складах, движений и записей в инвентаре сотрудников — можно удалить."
              onConfirm={() => handleDeleteMaterial(record.id)}
              okText="Удалить"
              cancelText="Отмена"
              okButtonProps={{ danger: true }}
            >
              <Button type="link" size="small" danger>
                Удалить
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <Preloader loading={loading}>
      <Tabs
        items={[
          {
            key: 'materials',
            label: 'Виды материалов (реестр)',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Button
                  type="primary"
                  onClick={() => {
                    setEditingMaterial(null);
                    setMatModalVisible(true);
                  }}
                >
                  Добавить вид материала
                </Button>
                <Table
                  rowKey="id"
                  size="small"
                  columns={matColumns}
                  dataSource={materials}
                  pagination={false}
                />
              </Space>
            ),
          },
          {
            key: 'categories',
            label: 'Категории',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="middle">
                <Button
                  type="primary"
                  onClick={() => {
                    setEditingCategory(null);
                    setCatModalVisible(true);
                  }}
                >
                  Добавить категорию
                </Button>
                <Table
                  rowKey="id"
                  size="small"
                  columns={catColumns}
                  dataSource={categories}
                  pagination={false}
                />
              </Space>
            ),
          },
          {
            key: 'warehouses',
            label: 'Склады',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <Button
                  type="primary"
                  onClick={() => {
                    setEditingWarehouse(null);
                    setWhModalVisible(true);
                  }}
                >
                  Добавить склад
                </Button>
                <Table
                  rowKey="id"
                  size="small"
                  columns={whColumns}
                  dataSource={warehouses}
                  pagination={false}
                />
              </Space>
            ),
          },
        ]}
      />

      <CategoryFormModal
        visible={catModalVisible}
        category={editingCategory}
        onClose={() => {
          setCatModalVisible(false);
          setEditingCategory(null);
        }}
        onSuccess={() => {
          setCatModalVisible(false);
          setEditingCategory(null);
          loadAll();
          if (onDataChange) onDataChange();
        }}
      />

      <WarehouseFormModal
        visible={whModalVisible}
        warehouse={editingWarehouse}
        onClose={() => {
          setWhModalVisible(false);
          setEditingWarehouse(null);
        }}
        onSuccess={() => {
          setWhModalVisible(false);
          setEditingWarehouse(null);
          loadAll();
          if (onDataChange) onDataChange();
        }}
      />

      <MaterialFormModal
        visible={matModalVisible}
        material={editingMaterial}
        variant="registry"
        onClose={() => {
          setMatModalVisible(false);
          setEditingMaterial(null);
        }}
        onSuccess={() => {
          setMatModalVisible(false);
          setEditingMaterial(null);
          loadAll();
          if (onDataChange) onDataChange();
        }}
      />
    </Preloader>
  );
};

export default ReferenceTab;
